import React from "react";
import { Link } from "react-router-dom";
import { TIERS } from "@/lib/wordLimits";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/74f34a17-8375-4051-bac6-d91db42d2320/images/819d568eacf9d82b1257629731d8ab0769d32d606df27875695363fdc54a65ae.png";

const CONVERSATION_PHOTO =
  "https://images.unsplash.com/photo-1542338332-76971ae8c292?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

export default function LandingPage() {
  return (
    <div className="fade-in" data-testid="landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 hero-grain"
          aria-hidden
        />
        <img
          src={HERO_BG}
          alt=""
          className="absolute inset-0 -z-10 w-full h-full object-cover opacity-30 mix-blend-multiply"
        />
        <div className="max-w-5xl mx-auto px-6 pt-20 pb-24 text-left">
          <p className="text-sm uppercase tracking-[0.18em] text-[color:var(--participant-a)] font-medium">
            Structured. Fair. Calm.
          </p>
          <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] mt-3 max-w-3xl text-[color:var(--text-primary)]">
            Resolve conflicts with{" "}
            <span className="italic text-[color:var(--participant-a)]">equal voice</span>,
            not louder volume.
          </h1>
          <p className="mt-6 text-lg text-[color:var(--text-muted)] max-w-2xl leading-relaxed">
            FairResolver guides two participants through an 8-stage mediation
            flow with clear word limits, gentle tone reminders, and neutral AI
            summaries — so both sides get heard before solutions are proposed.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/register"
              className="px-6 py-3 rounded-full bg-[color:var(--participant-a)] text-white font-semibold hover:opacity-90 transition"
              data-testid="hero-cta-register"
            >
              Start a free case
            </Link>
            <Link
              to="/pricing"
              className="px-6 py-3 rounded-full border border-[color:var(--line)] bg-white text-[color:var(--text-primary)] font-medium hover:bg-[color:var(--surface-sage)] transition"
              data-testid="hero-cta-pricing"
            >
              See word-limit tiers
            </Link>
          </div>
          <div className="mt-10 text-sm text-[color:var(--text-muted)] flex flex-wrap items-center gap-x-6 gap-y-2">
            <span>• 400-word opening story for every user</span>
            <span>• Turn-based, no interruptions</span>
            <span>• Save drafts, resume later</span>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--text-primary)] mb-4">
            How a fair conversation actually works.
          </h2>
          <p className="text-[color:var(--text-muted)] leading-relaxed mb-6">
            Each case begins with Stage 0 — the Initial Story Input. Both
            participants get up to <b>400 words</b> to explain their side,
            uninterrupted. After that, every following stage has its own
            word limit so no one dominates the conversation.
          </p>
          <ol className="space-y-3 text-sm">
            {[
              "Stage 0 — Initial Story (400 words each, no interruptions)",
              "Define the Issue",
              "Personal Impact",
              "Needs",
              "Acknowledgement",
              "Proposed Solution",
              "Negotiation",
              "Agreement",
            ].map((s, i) => (
              <li
                key={s}
                className="flex items-start gap-3 text-[color:var(--text-primary)]"
                data-testid={`how-step-${i}`}
              >
                <span className="mt-[3px] inline-flex items-center justify-center w-6 h-6 rounded-full bg-[color:var(--surface-sage)] text-xs font-semibold">
                  {i}
                </span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="relative">
          <img
            src={CONVERSATION_PHOTO}
            alt="Two people in calm conversation"
            className="rounded-2xl border border-[color:var(--line)] shadow-sm object-cover w-full h-[460px]"
          />
          <div className="absolute -bottom-5 -left-5 bg-white rounded-xl border border-[color:var(--line)] p-4 shadow-sm max-w-[260px]">
            <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)]">
              Word count
            </div>
            <div className="font-serif text-2xl mt-1">
              287 <span className="text-[color:var(--text-muted)]">/ 400</span>
            </div>
            <div className="text-xs text-[color:var(--counter-warning)] mt-1">
              You are close to the 400-word limit.
            </div>
          </div>
        </div>
      </section>

      {/* Tier teaser */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <h2 className="font-serif text-3xl text-[color:var(--text-primary)]">
            Tiers built around word limits.
          </h2>
          <Link
            to="/pricing"
            className="text-sm font-medium text-[color:var(--participant-a)] hover:underline"
            data-testid="pricing-link-teaser"
          >
            View full pricing →
          </Link>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          {TIERS.map((t) => (
            <div
              key={t.key}
              className="rounded-2xl border border-[color:var(--line)] bg-white p-5 hover:shadow-sm transition"
              data-testid={`tier-card-${t.key}`}
            >
              <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)]">
                {t.subtitle}
              </div>
              <div className="font-serif text-2xl mt-1">{t.name}</div>
              <div className="text-sm text-[color:var(--text-primary)] mt-1">
                {t.price}
              </div>
              <ul className="mt-4 space-y-1 text-sm text-[color:var(--text-muted)]">
                {t.bullets.slice(0, 3).map((b) => (
                  <li key={b}>• {b}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
